import { Badge } from "@comps/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import Field from "@dto/fields";
import { IsDarkColor } from "@utils/colors";
import { TW_ChoiceCell, TW_ChoiceEditionPopoverContent, TW_ChoiceItem, TW_ChoiceItemSelected, TW_ChoiceItemText, TW_ChoiceScrollArea, TW_CommonCommandInput, TW_UserBadge } from "@utils/twClasses";
import { containsPosition, nextPosition, posToString, prevPosition, samePosition } from "@utils/types";
import { memo, useEffect, useRef, useState } from "react";
import { useIsMobile } from "@comps/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useAppStore, useDataStore, useModelStore, useUserStore } from "src/store";
import { CellProps } from "./GridCell";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";
import User from "@dto/Users";


export function UserCell({ field, item, position, updateValue, isEditing}: CellProps) {

    const colNumber = useDataStore(state => state.colNumber);
    const edit = useDataStore(state => state.edit);
    const focus = useDataStore(state => state.focus);
    const [value, setValue] = useState<string | undefined>(item?.value ? item.value as string : undefined);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isMobile = useIsMobile();
    const [searchValue, setSearchValue] = useState("");
    const usersByKeys = useAppStore(state => state.usersByKeys);
    const users = Array.from(usersByKeys.values()).sort((a, b) => a.order - b.order);
    const wsColor = useModelStore(state => state.wsColor);
    const isDark = IsDarkColor(wsColor);
    const fontColor = isDark ? "white" : "black";
    const userRole = useModelStore(state => state.userRole);
    const currentUser = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(currentUser, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(currentUser, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;

    useEffect(() => {
        if (item?.value !== value) {
            setValue(item?.value ? item.value as string : undefined);
        }
    }, [item]);

    useEffect(() => {
        if (isEditing) {
            // Seed the search with the character typed to start editing (stored
            // by the grid in editInitialKey) so the first letter isn't lost.
            const initialKey = useDataStore.getState().editInitialKey;
            if (initialKey && initialKey.length === 1) {
                setSearchValue(initialKey);
            }
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [isEditing]);

    // console.log("Rendering UserCell");

    function onClick(event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) {
        const currentFocusPos = useDataStore.getState().focusPos;
        const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
        if (!readOnly && isSelected && !isEditing) {
            event.stopPropagation();
            event.preventDefault();
            edit(position);
        }
    }

    function validate(userKey: string) {

        // if the value is the same as the item value, do nothing
        if (userKey === value) {
            edit(position);
            return;
        }

        setSearchValue("");
        
        const newItem = { ...item, value: userKey };
        setValue(userKey);
        updateValue(newItem, userKey);
        focus(position);
    }


    // const options = field.typeItems!.get(TYPE_ITEMS_CHOICE_OPTIONS);
    // const choiceByNames = fieldChoiceByNames(options);

    function cancel() {
        setSearchValue("");
        focus(position);
    }

    function onEnterKeyPress(event: React.KeyboardEvent) {
        switch (event.key) {
            case "Backspace":
            case "Delete":
                if (!isEditing && !readOnly) {
                    validate("");
                }
                break;
            case "Enter":
                event.preventDefault();
                if (!isEditing && !readOnly) {
                    edit(position);
                }
                break;
            case "Tab":
                let next = null;
                if (event.shiftKey) {
                    next = prevPosition(position, colNumber);
                } else {
                    next = nextPosition(position, colNumber);
                }
                focus(next);
                break;
            case "Escape":
                event.preventDefault();
                cancel();
                break;
            default:
                // Fallback when the trigger itself is focused (the grid normally
                // routes the first keystroke through GridCell, which seeds via
                // editInitialKey). The input is controlled, so seed the state —
                // assigning .value directly was the original lost-first-letter bug.
                if (!isEditing && !readOnly && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    setSearchValue(event.key);
                    edit(position);
                }
        }
    }

    function setIsDialogOpen(isOpen: boolean) {
        if (isOpen) {
            const currentFocusPos = useDataStore.getState().focusPos;
            const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
            if (!readOnly && isSelected && !isEditing) {
                edit(position);
            }
        } else {
            // Moving the focus to another cell (Tab) closes the popover: it stays there
            const currentFocusPos = useDataStore.getState().focusPos;
            if (!currentFocusPos || containsPosition(currentFocusPos, position)) {
                focus(position);
            }
        }
    }

    const user = value ? usersByKeys.get(value) : undefined;
    const username = user?.username;
    const badgeStyle = {
        backgroundColor: wsColor,
        color: fontColor
    }
    return (
            <Popover open={isEditing} onOpenChange={setIsDialogOpen}>
                <PopoverTrigger className={TW_ChoiceCell + (readOnly ? " cursor-not-allowed" : "")} onKeyDown={onEnterKeyPress} onClick={onClick} tabIndex={0}>
                    <div key={posToString(position) + "_div_" + field._key} className="flex flex-row justify-end items-center gap-1">
                        {username &&
                            <Badge key={posToString(position) + "_badge_" + field._key + "_" + value} className={TW_UserBadge + (readOnly ? " cursor-not-allowed" : "")} style={badgeStyle}>{username}</Badge>
                        }
                    </div>
                </PopoverTrigger>
                <PopoverContent className={TW_ChoiceEditionPopoverContent} side={isMobile ? "bottom" : "right"}>
                    <UsersEditionForm field={field} value={value} users={users} updateFieldChoices={validate} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} />
                </PopoverContent>
            </Popover>
    )

}


interface UsersEditionFormProps {
    field: Field,
    value: string | undefined,
    users: User[],
    updateFieldChoices: (userKey: string) => void,
    searchValue: string,
    setSearchValue: (searchValue: string) => void,
    searchInputRef: React.RefObject<HTMLInputElement>
}

const UsersEditionForm = memo(function UsersEditionForm({ field, value, users, updateFieldChoices, searchValue, setSearchValue, searchInputRef }: UsersEditionFormProps) {

    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);

    const onUserSelected = (userSelected: string) => {

        const wasChoosen = value === userSelected;
        const choosenUser = users?.find(user => user._key === userSelected);

        if (!choosenUser) {
            return;
        }

        let newChoices = value ? value : "";
        if (wasChoosen) {
            newChoices = "";
        } else {
            newChoices = choosenUser._key;
        }

        updateFieldChoices(newChoices);
    }

    const onKeyDown = (event: React.KeyboardEvent) => {
        // event.preventDefault();
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                setSearchValue("");
                break;
        }
    }

    return (

        <Command onKeyDown={onKeyDown}>
            <CommandInput 
                placeholder={t("data.choice.search.placeholder")} 
                className={TW_CommonCommandInput} 
                autoFocus={true} 
                value={searchValue} 
                onValueChange={(value) => setSearchValue(value)} 
                ref={searchInputRef} />
            <div className={TW_ChoiceScrollArea}>
                <CommandList className="h-full max-h-full w-full">
                    <CommandEmpty>{t("data.table.noChoiceItems")}</CommandEmpty>
                    <CommandGroup>
                        {users.map((user: User, index: number) => {
                            const username = user.username;
                            const isChoosen = user._key === value;
                            let optionStyle = undefined;
                            if (isChoosen) {
                                optionStyle = { borderColor: wsColor, color: wsColor};
                            } else {
                                optionStyle = { color: 'black'};
                            }
                            const wrapperStyle = {...optionStyle, borderWidth: 2 };
                            return (
                                <CommandItem
                                    key={user._key}
                                    className={`${isChoosen ? TW_ChoiceItemSelected : TW_ChoiceItem}`}
                                    style={wrapperStyle}
                                    onSelect={() => onUserSelected(user._key)}>
                                    <span className={TW_ChoiceItemText} style={optionStyle}>{username}</span>
                                </CommandItem>
                            )
                        })}
                    </CommandGroup>
                </CommandList>
            </div>
        </Command>

    )
}, (prevProps, nextProps) => {
    return prevProps.field === nextProps.field && prevProps.value === nextProps.value && prevProps.users === nextProps.users && prevProps.searchValue === nextProps.searchValue;
});
